import { useEffect, useMemo, useState } from 'react';

type RuntimeLoader = () => Promise<{ default: React.ComponentType }>;

const defaultLoader: RuntimeLoader = () => import('./LandingBoardRuntime');

export default function LandingBoardLazy({ loadRuntime = defaultLoader }: { loadRuntime?: RuntimeLoader }) {
    const [loadFailed, setLoadFailed] = useState(false);
    const [runtime, setRuntime] = useState<React.ComponentType | null>(null);
    const placeholder = useMemo(
        () => (
            <div aria-label="Loading board preview" data-testid="landing-board-placeholder">
                <svg
                    aria-hidden="true"
                    height="220"
                    role="presentation"
                    viewBox="0 0 340 220"
                    width="340"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <rect fill="#F3F4F6" height="220" rx="12" width="340" />
                    <circle cx="70" cy="60" fill="#E5E7EB" r="8" />
                    <circle cx="120" cy="85" fill="#E5E7EB" r="8" />
                    <circle cx="170" cy="60" fill="#E5E7EB" r="8" />
                    <circle cx="220" cy="85" fill="#E5E7EB" r="8" />
                    <circle cx="270" cy="60" fill="#E5E7EB" r="8" />
                    <rect fill="#E5E7EB" height="16" rx="8" width="240" x="50" y="180" />
                </svg>
            </div>
        ),
        []
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        let active = true;
        let invoked = false;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let idleId: number | null = null;

        const load = () => {
            if (invoked) {
                return;
            }
            invoked = true;

            loadRuntime()
                .then(mod => {
                    if (active) {
                        setRuntime(() => mod.default);
                    }
                })
                .catch(() => {
                    if (active) {
                        setLoadFailed(true);
                    }
                });
        };

        const requestIdle = (window as any).requestIdleCallback as
            | ((cb: IdleRequestCallback, opts?: IdleRequestOptions) => number)
            | undefined;

        if (requestIdle) {
            idleId = requestIdle(load, { timeout: 750 });
            timeoutId = setTimeout(load, 1000);
        } else {
            timeoutId = setTimeout(load, 250);
        }

        return () => {
            active = false;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            const cancelIdle = (window as any).cancelIdleCallback as ((id: number) => void) | undefined;
            if (idleId !== null && cancelIdle) {
                cancelIdle(idleId);
            }
        };
    }, [loadRuntime]);

    if (loadFailed || !runtime) {
        return placeholder;
    }

    const Runtime = runtime;
    return <Runtime />;
}
